import { createContext, useContext, ReactNode } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "store";
import { logout as reduxLogout } from "store/slices/authSlice";
import { authApi } from "store/services/authApi";

interface AuthContextType {
  user: any;
  login: (email: string, password: string) => boolean;
  logout: () => void;
  isAuthenticated: boolean;
  isVerified: boolean;
  userRole: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { user, isAuthenticated, isVerified } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();

  const [endSession] = authApi.useEndSessionMutation();

  const login = (email: string, password: string): boolean => {
    // This is now handled by Redux mutation in Login.tsx
    // Keep this for backward compatibility if needed, but it won't do much
    return false;
  };

  const logout = async () => {
    try {
      await endSession(undefined).unwrap();
    } catch (err) {
      console.error("Failed to end session on server:", err);
    } finally {
      dispatch(reduxLogout());
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        isAuthenticated,
        isVerified,
        userRole: user?.role?.name?.toLowerCase() || null,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
